/**
 * TODO: Once briefings V1 is removed, we can rename this file to `useAudio` so that it is more feature-agnostic.
 *
 * The reason we are not renaming it now is to reduce friction with the existing Briefings V1 feature
 * and thus just adding the activeFeature state and actions here.
 *
 * Once Briefings V1 is removed, we can update the name of this hook and also add a discriminated union
 * for all audio types and export feature specific hooks that converge onto the new `useAudio` hook.
 *
 * Part of reason we are not building on the new hook now is because some of the existing subcomponents
 * such as ToneSelector are used by both Briefings V1 and V2, so introduceing a new hook would mean that
 * these subcomponents would need to be either duplicated or call two state hooks.
 */

import { createStore, createHook, createContainer, createActionsHook } from 'react-sweet-state';
import type { Action } from 'react-sweet-state';

import {
	confluenceLocalStorageInstance as localStorage,
	keys as localStorageKeys,
} from '@confluence/storage-manager';

import { getBriefing } from '../api/speechAPI';
import type { AudioBriefingRequest } from '../types/audioRequestType';
import type { ActiveAudioContent } from '../types/activeAudioContent';
import type { CustomizationType } from '../types/customizationTypes';
import type { AdditionalResource } from '../types/additionalResource';
import { CustomTone, CustomLength } from '../types/customizationTypes';
import { getConfluenceContentAri } from '../utils/getConfluenceContentAri';
import type { AudioBriefingErrorType } from '../types/audioBriefingErrorType';
import type { AudioFeatureType } from '../types/audioFeatureType';
import { AudioFeature } from '../types/audioFeatureType';
import { AudioBriefingError } from '../types/audioBriefingErrorType';
import { determineErrorType } from '../utils/determineErrorType';

export type LastGeneratedBriefingConfig = {
	additionalResources: AdditionalResource[];
	customization: CustomizationType;
};
export type AudioBriefingState = {
	activeContent?: ActiveAudioContent;
	activeFeature?: AudioFeatureType;
	cloudId: string;
	activationId: string;
	locale: string;
	isLoading: boolean;
	isPlaying: boolean;
	isClosed: boolean;
	isEnded: boolean;
	hasEndBeenReached: boolean;
	hasFetched: boolean;
	playbackSpeed: number;
	error?: Error;
	errorType?: AudioBriefingErrorType;
	stream?: ReadableStream<Uint8Array>;
	streamEnded: boolean;

	// Briefing-specific state
	customization: CustomizationType;
	additionalResources: AdditionalResource[];
	lastGeneratedBriefingConfig?: LastGeneratedBriefingConfig;
};

export type FetchAudioOptions = {
	feature: AudioFeatureType;
};

export type AudioBriefingInitialStateInput = Required<
	Pick<AudioBriefingState, 'activeContent' | 'cloudId' | 'activationId' | 'locale'>
>;

const DEFAULT_CUSTOMIZATION: CustomizationType = {
	tone: CustomTone.PROFESSIONAL,
	length: CustomLength.SHORT,
};

const DEFAULT_PLAYBACK_SPEED = 1;

const safeParseJson = (json: string) => {
	try {
		return JSON.parse(json);
	} catch (e) {
		return DEFAULT_CUSTOMIZATION;
	}
};

const initialAudioBriefingState: AudioBriefingState = {
	activeContent: undefined,
	activeFeature: undefined,
	customization: DEFAULT_CUSTOMIZATION,
	additionalResources: [],
	cloudId: '',
	activationId: '',
	locale: 'en-US',
	isLoading: false,
	isPlaying: false,
	isClosed: false,
	isEnded: false,
	hasEndBeenReached: false,
	hasFetched: false,
	lastGeneratedBriefingConfig: undefined,
	playbackSpeed: DEFAULT_PLAYBACK_SPEED,
	stream: undefined,
	error: undefined,
	errorType: undefined,
	streamEnded: false,
};

type AudioBriefingContainerProps = {
	initialAudioBriefingState: AudioBriefingInitialStateInput;
};

type AudioBriefingActions = {
	initialize: (
		initialState: AudioBriefingInitialStateInput,
	) => Action<AudioBriefingState, void, void>;
	addAdditionalResource: (
		newAdditionalResource: AdditionalResource,
	) => Action<AudioBriefingState, void, void>;
	removeAdditionalResource: (resourceToRemove: string) => Action<AudioBriefingState, void, void>;
	reset: () => Action<AudioBriefingState, void, void>;
	fetchAudio: (options: FetchAudioOptions) => Action<AudioBriefingState, void, void>;
	// Convenience wrapper for the legacy briefing feature. Delegates to `fetchAudio` with the
	// `BRIEFING` feature pre-selected so existing V1 call-sites don’t have to pass a feature param.
	// TODO: Remove this once we've migrated all call-sites to use the new `fetchAudio` API.
	fetchBriefing: () => Action<AudioBriefingState, void, void>;
	onLoadStart: (feature: AudioFeatureType) => Action<AudioBriefingState, void, void>;
	onLoadEnd: () => Action<AudioBriefingState, void, void>;
	pauseContent: () => Action<AudioBriefingState, void, void>;
	resumeContent: () => Action<AudioBriefingState, void, void>;
	setContentPlaybackSpeed: (speed: number) => Action<AudioBriefingState, void, void>;
	stopContent: () => Action<AudioBriefingState, void, void>;
	endContent: () => Action<AudioBriefingState, void, void>;
	setContentError: (error: Error) => Action<AudioBriefingState, void, void>;
	setStreamEnded: (streamEnded: boolean) => Action<AudioBriefingState, void, void>;
	setCustomization: (
		customization: Partial<CustomizationType>,
	) => Action<AudioBriefingState, void, void>;
};

export const audioBriefingActions: AudioBriefingActions = {
	initialize:
		(initialState: AudioBriefingInitialStateInput) =>
		async ({ setState }) => {
			const storedCustomization = localStorage.getItem(
				localStorageKeys.AI_AUDIO_BRIEFING_CUSTOMIZATION,
			);
			const parsedCustomization = storedCustomization
				? safeParseJson(storedCustomization)
				: DEFAULT_CUSTOMIZATION;

			const emptyAudioBriefingState: AudioBriefingState = {
				stream: undefined,
				streamEnded: false,
				activeContent: undefined,
				customization: parsedCustomization,
				additionalResources: [],
				locale: 'en-US',
				cloudId: '',
				activationId: '',
				isLoading: false,
				isPlaying: false,
				isClosed: false,
				isEnded: false,
				hasEndBeenReached: false,
				hasFetched: false,
				error: undefined,
				errorType: undefined,
				playbackSpeed:
					Number(localStorage.getItem(localStorageKeys.AI_AUDIO_PLAYBACK_SPEED)) ||
					DEFAULT_PLAYBACK_SPEED,
			};
			setState(Object.assign(emptyAudioBriefingState, initialState));
		},
	reset:
		() =>
		({ setState }) => {
			setState(initialAudioBriefingState);
		},
	addAdditionalResource:
		(newAdditionalResource: AdditionalResource) =>
		({ getState, setState }) => {
			const { additionalResources } = getState();
			setState({
				additionalResources: [...additionalResources, newAdditionalResource],
			});
		},
	removeAdditionalResource:
		(ariToRemove: string) =>
		({ getState, setState }) => {
			const { additionalResources } = getState();
			const newResourcesList = additionalResources.filter(
				(resource) => resource.ari !== ariToRemove,
			);
			setState({
				additionalResources: newResourcesList,
			});
		},
	fetchBriefing:
		() =>
		async ({ dispatch }) => {
			dispatch(audioBriefingActions.fetchAudio({ feature: AudioFeature.BRIEFING }));
		},
	fetchAudio:
		({ feature }: FetchAudioOptions) =>
		async ({ getState, setState, dispatch }) => {
			dispatch(audioBriefingActions.onLoadStart(feature));

			const { activeContent, additionalResources, cloudId, activationId, locale, customization } =
				getState();

			if (!activeContent) {
				dispatch(
					audioBriefingActions.setContentError(
						new Error('Briefing container needs to be initialized'),
					),
				);
				return;
			}

			const activeContentAri = getConfluenceContentAri(
				cloudId,
				activeContent.id,
				activeContent.contentType,
			);

			if (!activeContentAri) {
				dispatch(audioBriefingActions.setContentError(new Error('Content ARI failed to generate')));
				return;
			}

			try {
				if (feature === AudioFeature.BRIEFING) {
					const audioRequest: AudioBriefingRequest = {
						activeContentAri,
						additionalResources,
						activationId,
						locale,
						customization,
					};
					const stream = await getBriefing(audioRequest);

					if (getState().isClosed) {
						return;
					}

					setState({
						isPlaying: false,
						isEnded: false,
						hasEndBeenReached: false,
						stream,
						lastGeneratedBriefingConfig: {
							additionalResources,
							customization,
						},
					});
				}
			} catch (e) {
				const errorType = await determineErrorType(e);
				setState({
					error: e,
					errorType,
					stream: undefined,
				});
			} finally {
				dispatch(audioBriefingActions.onLoadEnd());
			}
		},
	onLoadStart:
		(feature: AudioFeatureType) =>
		({ setState }) => {
			setState({
				activeFeature: feature,
				isLoading: true,
				hasFetched: true,
				error: undefined,
				errorType: undefined,
				streamEnded: false,
			});
		},
	onLoadEnd:
		() =>
		({ setState }) => {
			setState({ isLoading: false });
		},
	pauseContent:
		() =>
		({ setState }) => {
			setState({
				isPlaying: false,
			});
		},
	resumeContent:
		() =>
		({ setState, getState }) => {
			const { stream } = getState();
			if (!stream) {
				throw new Error('Must fetch before playing');
			}
			setState({
				isPlaying: true,
				isEnded: false,
			});
		},
	setContentPlaybackSpeed:
		(speed: number) =>
		({ setState }) => {
			localStorage.setItem(localStorageKeys.AI_AUDIO_PLAYBACK_SPEED, speed);
			setState({ playbackSpeed: speed });
		},
	stopContent:
		() =>
		async ({ dispatch, setState }) => {
			dispatch(audioBriefingActions.reset());
			setState({ isClosed: true });
		},
	endContent:
		() =>
		({ setState }) => {
			setState({
				isEnded: true,
				hasEndBeenReached: true,
				isPlaying: false,
			});
		},
	setContentError:
		(error: Error) =>
		({ setState }) => {
			setState({
				error,
				errorType: AudioBriefingError.GENERAL_ERROR,
			});
		},
	setStreamEnded:
		(streamEnded: boolean) =>
		({ setState }) => {
			setState({
				streamEnded,
			});
		},
	setCustomization:
		(newCustomization: Partial<CustomizationType>) =>
		({ setState, getState }) => {
			const { customization } = getState();
			const updatedCustomization = { ...customization, ...newCustomization };

			localStorage.setItem(
				localStorageKeys.AI_AUDIO_BRIEFING_CUSTOMIZATION,
				JSON.stringify({
					tone: updatedCustomization.tone,
					length: updatedCustomization.length,
				}),
			);
			setState({ customization: updatedCustomization });
		},
};

// Exported for tests
export const Store = createStore<AudioBriefingState, AudioBriefingActions>({
	initialState: {
		activeContent: undefined,
		customization: DEFAULT_CUSTOMIZATION,
		additionalResources: [],
		cloudId: '',
		activationId: '',
		locale: 'en-US',
		isLoading: false,
		isPlaying: false,
		isClosed: false,
		isEnded: false,
		hasEndBeenReached: false,
		hasFetched: false,
		playbackSpeed: 1,
		error: undefined,
		errorType: undefined,
		streamEnded: false,
	},
	actions: audioBriefingActions,
	name: 'listenerState',
});

export const AudioBriefingContainer = createContainer<
	AudioBriefingState,
	AudioBriefingActions,
	AudioBriefingContainerProps
>(Store, {
	onInit:
		() =>
		async ({ dispatch }, { initialAudioBriefingState: initialAudioPlaybackState }) => {
			void dispatch(audioBriefingActions.initialize(initialAudioPlaybackState));
		},
});

export const useAudioBriefing = createHook<AudioBriefingState, AudioBriefingActions>(Store);
export const useAudioBriefingActions = createActionsHook(Store);
